//
// # Error Utilities
//
// Helpers for extracting human-readable messages from nested server error objects.
//

// Extract a human-readable message from a nested error object.
//
// Server errors arrive as nested structures like:
//   { why: "add-dummy-devices-failed",
//     data: { why: "Failed to update source instance ndum: {\"why\":...}" } }
//
// Sometimes the inner object gets JSON.stringify'd into a `why` string,
// producing "Failed to ...: {\"why\":\"...\",\"data\":{\"errors\":[...]}}".
//
// Strategy: collect all plain-text `why` values and `errors` arrays from the
// chain — including parsing embedded JSON tails — then present them as a
// short readable summary.
export function extractErrorMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (typeof err !== "object" || err === null) return String(err);

  const reasons: string[] = [];
  const errors: string[] = [];
  collectReasons(err, reasons, errors);

  // Handlers wrap a cause in layers of machine tags on the way up, so the
  // chain reads "failed-to-create-instance: generator-creation-failed:
  // generation-failed: <the sentence that actually explains it>". The tags
  // only restate "it failed" — drop them once a real sentence is present.
  // When it's tags all the way down, keep them: a terse tag beats no message.
  // Collected `errors` (e.g. per-param validation messages) count as a real
  // explanation too, so tags get dropped in that case as well.
  const sentences = reasons.filter(isHumanSentence);
  const shown =
    sentences.length > 0 ? sentences : errors.length > 0 ? [] : reasons;

  const parts: string[] = [];
  if (shown.length > 0) parts.push(shown.join(": "));
  if (errors.length > 0) parts.push(errors.join("; "));

  return parts.length > 0 ? parts.join(" — ") : JSON.stringify(err);
}

// A machine tag is a single kebab-case token ("generation-failed"); a
// human-readable cause has whitespace in it.
function isHumanSentence(reason: string): boolean {
  return /\s/.test(reason.trim());
}

// Recursively walk the error chain collecting human-readable pieces.
// - `why` strings that look like plain text (no embedded JSON) are kept as-is
// - `why` strings with embedded JSON are split: the text prefix is kept,
//   and the JSON tail is parsed and recursed into
// - `errors` arrays are collected as validation messages
// - `message` strings (e.g. from Error instances) are kept
// - `data` sub-objects are recursed into
function collectReasons(
  err: unknown,
  reasons: string[],
  errors: string[]
): void {
  if (typeof err !== "object" || err === null) return;

  const obj = err as Record<string, unknown>;

  if (typeof obj.why === "string") {
    const jsonStart = obj.why.indexOf("{");
    if (jsonStart === -1) {
      // Plain text why — keep it
      reasons.push(obj.why);
    } else {
      // Has embedded JSON — extract the text prefix and parse the tail
      const prefix = obj.why
        .slice(0, jsonStart)
        .replace(/[:\s]+$/, "")
        .trim();
      if (prefix) reasons.push(prefix);
      try {
        const embedded = JSON.parse(obj.why.slice(jsonStart));
        collectReasons(embedded, reasons, errors);
      } catch {
        // JSON parse failed — just use the whole string
        if (!prefix) reasons.push(obj.why);
      }
    }
  }

  // Collect `message` strings (e.g. from Error instances)
  if (typeof obj.message === "string" && !obj.why) {
    reasons.push(obj.message);
  }

  // Collect validation errors
  if (Array.isArray(obj.errors)) {
    for (const e of obj.errors) {
      if (typeof e === "string") errors.push(e);
    }
  }

  // Recurse into nested data and error sub-objects
  if (obj.data && typeof obj.data === "object") {
    collectReasons(obj.data, reasons, errors);
  }
  if (obj.error && typeof obj.error === "object") {
    collectReasons(obj.error, reasons, errors);
  }
}

// Whether `why` appears as a `why` tag anywhere in a nested error, walking
// `data` the way handlers wrap a cause on the way up. For telling one failure
// apart (e.g. `oa-cellview-exists`, which asks the user a question) without
// matching on message text.
export function hasErrorWhy(err: unknown, why: string): boolean {
  let current: unknown = err;
  // Bounded: a cyclic error must not hang the caller.
  for (let depth = 0; depth < 16; depth++) {
    if (typeof current !== "object" || current === null) return false;
    if (Reflect.get(current, "why") === why) return true;
    current = Reflect.get(current, "data");
  }
  return false;
}
